import { useState } from 'react';
import { Search, ShoppingCart, Cpu, Award, Zap, Shield, Code, Terminal, Brain, Filter, ChevronDown, ChevronRight, Star } from 'lucide-react';

export default function FMarket() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const categories = [
    { id: 'all', name: 'All Agents', icon: <Cpu /> },
    { id: 'coding', name: 'Coding', icon: <Code /> },
    { id: 'analysis', name: 'Data Analysis', icon: <Terminal /> },
    { id: 'creative', name: 'Creative', icon: <Brain /> },
    { id: 'security', name: 'Security', icon: <Shield /> }
  ];
  
  const agents = [
    {
      id: 1,
      name: "CodeForge",
      category: "coding",
      description: "Builds robust applications in multiple languages",
      price: 350,
      level: 4,
      rating: 4.8,
      powerLevel: 85
    },
    {
      id: 2,
      name: "DataSeer",
      category: "analysis",
      description: "Transforms raw data into actionable insights",
      price: 290,
      level: 3,
      rating: 4.5,
      powerLevel: 75
    },
    {
      id: 3,
      name: "NeuralArtist",
      category: "creative",
      description: "Generates stunning visuals and creative content",
      price: 320,
      level: 4,
      rating: 4.6,
      powerLevel: 80
    },
    {
      id: 4,
      name: "SentinelAI",
      category: "security",
      description: "Monitors and protects your digital assets",
      price: 380,
      level: 5,
      rating: 4.9,
      powerLevel: 90
    },
    {
      id: 5,
      name: "CodeWizard",
      category: "coding",
      description: "Optimizes and refactors legacy systems",
      price: 340,
      level: 3,
      rating: 4.7,
      powerLevel: 82
    },
    {
      id: 6,
      name: "InsightEngine",
      category: "analysis",
      description: "Predicts trends with advanced machine learning",
      price: 310,
      level: 4,
      rating: 4.4,
      powerLevel: 78
    }
  ];
  
  const filteredAgents = selectedCategory === 'all' 
    ? agents 
    : agents.filter(agent => agent.category === selectedCategory);
  
  return (
    <div className="min-h-screen bg-black text-gray-200 font-mono">
      {/* Header */}
      <header className="border-b border-gray-800 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Terminal className="text-emerald-500" size={28} />
            <h1 className="text-2xl font-bold text-emerald-400">FOUNDRY<span className="text-gray-400">_MARKETPLACE</span></h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search agents..."
                className="bg-gray-900 border border-gray-700 rounded-md px-4 py-2 pl-10 focus:outline-none focus:ring-1 focus:ring-emerald-500 w-64"
              />
              <Search className="absolute left-3 top-2.5 text-gray-500" size={18} />
            </div>
            <button className="bg-gray-800 p-2 rounded-md hover:bg-gray-700 transition">
              <ShoppingCart className="text-emerald-400" size={20} />
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Categories */}
        <div className="flex mb-8 space-x-2 overflow-x-auto scrollbar-hide">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition ${
                selectedCategory === category.id 
                  ? 'bg-emerald-900 text-emerald-400 border border-emerald-700' 
                  : 'bg-gray-900 border border-gray-800 hover:bg-gray-800'
              }`}
            >
              {category.icon}
              <span>{category.name}</span>
            </button>
          ))}
          
          <button className="flex items-center space-x-2 px-4 py-2 rounded-md bg-gray-900 border border-gray-800 hover:bg-gray-800 transition ml-auto">
            <Filter size={18} />
            <span>Filter</span>
            <ChevronDown size={16} />
          </button>
        </div>
        
        {/* Featured Agent */}
        <div className="mb-12 bg-gradient-to-r from-gray-900 to-gray-950 rounded-lg p-6 border border-gray-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 blur-3xl rounded-full"></div>
          <div className="flex items-start">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="text-yellow-500" size={20} />
                <span className="text-yellow-500 font-bold">FEATURED AGENT</span>
              </div>
              <h2 className="text-3xl font-bold mb-2 text-white">Nexus<span className="text-emerald-400">Prime</span></h2>
              <p className="text-gray-400 mb-4 max-w-2xl">The ultimate orchestrator. Coordinates multiple specialized agents to solve complex problems across domains.</p>
              
              <div className="flex items-center space-x-6">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Power Level</div>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                    <span className="text-emerald-400 font-bold">95</span>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500 mb-1">Level</div>
                  <div className="flex items-center">
                    <span className="text-white font-bold">MAX</span>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500 mb-1">Rating</div>
                  <div className="flex items-center">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className="text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    <span className="ml-1 text-white font-bold">5.0</span>
                  </div>
                </div>
              </div>
              
              <button className="mt-6 bg-emerald-600 hover:bg-emerald-500 text-black font-bold px-6 py-3 rounded-md transition flex items-center space-x-2">
                <Zap size={18} />
                <span>Deploy Agent • 750 CREDITS</span>
              </button>
            </div>
            
            <div className="hidden md:block w-64 h-64 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-full flex items-center justify-center">
              <div className="w-48 h-48 rounded-full bg-gray-900 flex items-center justify-center relative">
                <div className="absolute inset-0 rounded-full border-4 border-emerald-500/30 animate-spin-slow"></div>
                <Brain size={64} className="text-emerald-400" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Agent Grid */}
        <h2 className="text-xl font-bold mb-6 flex items-center">
          <span className="text-emerald-400 mr-2">#</span> Available Agents
          <span className="text-gray-500 text-sm ml-2">({filteredAgents.length})</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map(agent => (
            <div 
              key={agent.id}
              className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition group"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-md bg-gray-800 flex items-center justify-center">
                    {agent.category === 'coding' && <Code className="text-blue-400" size={20} />}
                    {agent.category === 'analysis' && <Terminal className="text-purple-400" size={20} />}
                    {agent.category === 'creative' && <Brain className="text-pink-400" size={20} />}
                    {agent.category === 'security' && <Shield className="text-red-400" size={20} />}
                  </div>
                  <div>
                    <h3 className="font-bold text-white group-hover:text-emerald-400 transition">{agent.name}</h3>
                    <div className="text-xs text-gray-500">Level {agent.level}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex">
                    {[...Array(Math.floor(agent.rating))].map((_, i) => (
                      <Star key={i} size={12} className="text-yellow-500 fill-yellow-500" />
                    ))}
                    {agent.rating % 1 > 0 && (
                      <Star size={12} className="text-yellow-500 fill-yellow-500 opacity-50" />
                    )}
                  </div>
                  <span className="ml-1 text-xs text-gray-400">{agent.rating}</span>
                </div>
              </div>
              
              <p className="text-gray-400 text-sm mb-4">{agent.description}</p>
              
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-1">Power</div>
                <div className="flex items-center space-x-2">
                  <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full" 
                      style={{ width: `${agent.powerLevel}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-emerald-400 font-bold">{agent.powerLevel}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="text-lg font-bold text-white">{agent.price} <span className="text-xs text-gray-500">CREDITS</span></div>
                <button className="bg-gray-800 hover:bg-emerald-900 border border-gray-700 hover:border-emerald-600 text-gray-300 hover:text-emerald-400 px-3 py-1.5 rounded text-sm flex items-center space-x-1 transition">
                  <span>Deploy</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}